/**
 * ProductForm — custom element that owns:
 *   - variant selection (button pills, swatches, dropdowns)
 *   - add-to-cart fetch + loading state
 *   - price / availability / SKU / stock updates
 *   - gallery sync (thumbnail clicks, touch swipe, dot nav)
 *   - sticky ATC bar (IntersectionObserver)
 *   - share button (Clipboard API)
 *   - quantity stepper
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.sectionId        = this.dataset.sectionId;
    this.form             = this.querySelector('form');
    this.submitBtn        = this.querySelector('.product__add-btn');
    this.submitBtnText    = this.querySelector('.product__add-btn-text');
    this.submitBtnSpinner = this.querySelector('.product__add-btn-spinner');
    this.errorEl          = document.getElementById('product-error-' + this.sectionId);
    this.priceEl          = document.getElementById('product-price-' + this.sectionId);
    this.skuEl            = document.getElementById('product-sku-' + this.sectionId);
    this.stickyBtn        = document.getElementById('product-sticky-btn-' + this.sectionId);
    this.stickyPrice      = document.getElementById('product-sticky-price-' + this.sectionId);
    this.galleryEl        = document.getElementById('product-gallery-' + this.sectionId);
    this.thumbnails       = document.querySelectorAll('.product__thumb');

    let dataEl = document.getElementById('product-data-' + this.sectionId);
    if (!dataEl) return;
    this.productData    = JSON.parse(dataEl.textContent);
    this.currentVariant = this.productData.variants.find(function(v) {
      return v.id == this.form.querySelector('[name="id"]').value;
    }, this);
    this.moneyFormat = document.body.dataset.moneyFormat || '';

    this.setupOptionButtons();
    this.setupQuantity();
    this.setupGallery();
    this.setupStickyBar();
    this.setupShare();

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  /* ── Variant selection ────────────────────────────────────── */

  setupOptionButtons() {
    this.querySelectorAll('.product__option-btn, .product__swatch').forEach(function(btn) {
      btn.addEventListener('click', function() { this.onOptionChange(btn); }.bind(this));
    }, this);
    this.querySelectorAll('.product__option-select').forEach(function(sel) {
      sel.addEventListener('change', function() { this.onSelectChange(sel); }.bind(this));
    }, this);
  }

  onSelectChange(sel) {
    let position = sel.dataset.optionPosition;
    let value    = sel.value;
    let valueEl  = document.getElementById('option-value-' + this.sectionId + '-' + (parseInt(position) - 1));
    if (valueEl) valueEl.textContent = value;

    let selectedOptions = {};
    this.querySelectorAll('.product__option-select').forEach(function(s) {
      selectedOptions[s.dataset.optionPosition] = s.value;
    });

    let newVariant = this._matchVariant(selectedOptions);
    if (newVariant) {
      this.currentVariant = newVariant;
      this.updatePrice(newVariant);
      this.updateAvailability(newVariant);
      this.updateSKU(newVariant);
      this.updateVariantImage(newVariant);
      this.updateURL(newVariant);
      this.updateStickyBar(newVariant);
      this.updateStock(newVariant);
    }
  }

  onOptionChange(btn) {
    let position = btn.dataset.optionPosition;
    this.querySelectorAll('[data-option-position="' + position + '"]').forEach(function(b) {
      b.classList.toggle('is-active', b === btn);
      b.setAttribute('aria-pressed', b === btn);
    });

    let valueEl = document.getElementById('option-value-' + this.sectionId + '-' + (parseInt(position) - 1));
    if (valueEl) valueEl.textContent = btn.dataset.value;

    let selectedOptions = {};
    this.querySelectorAll('.product__option-btn.is-active, .product__swatch.is-active').forEach(function(b) {
      selectedOptions[b.dataset.optionPosition] = b.dataset.value;
    });

    let newVariant = this._matchVariant(selectedOptions);
    if (newVariant) {
      this.currentVariant = newVariant;
      this.updatePrice(newVariant);
      this.updateAvailability(newVariant);
      this.updateSKU(newVariant);
      this.updateVariantImage(newVariant);
      this.updateURL(newVariant);
      this.form.querySelector('[name="id"]').value = newVariant.id;
    }
  }

  _matchVariant(selectedOptions) {
    for (let i = 0; i < this.productData.variants.length; i++) {
      let v     = this.productData.variants[i];
      let match = true;
      for (let pos in selectedOptions) {
        if (v['option' + pos] !== selectedOptions[pos]) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  }

  /* ── Money formatting ─────────────────────────────────────── */

  formatMoney(cents) {
    let value           = cents / 100;
    let fmt             = this.moneyFormat || '{{amount}}';
    let withDecimals    = value.toFixed(2);
    let noDecimals      = String(Math.round(value));
    let withComma       = value.toFixed(2).replace('.', ',');
    let noDecimalsComma = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    let withApostrophe  = value.toFixed(2).replace('.', '\u2019');
    let withSpace       = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
    return fmt
      .replace('{{amount_with_comma_separator}}',             withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimalsComma)
      .replace('{{amount_with_apostrophe_separator}}',        withApostrophe)
      .replace('{{amount_no_decimals_with_space_separator}}', withSpace)
      .replace('{{amount_no_decimals}}',                      noDecimals)
      .replace('{{amount}}',                                  withDecimals);
  }

  /* ── DOM update methods ───────────────────────────────────── */

  updatePrice(variant) {
    if (!this.priceEl) return;
    let formattedPrice = this.formatMoney(variant.price);
    let html = '';
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      let savingsPct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price);
      html += '<span class="product__price-sale">' + formattedPrice + '</span>';
      html += '<s class="product__price-compare">' + this.formatMoney(variant.compare_at_price) + '</s>';
      html += '<span class="badge badge--sale">-' + savingsPct + '%</span>';
    } else {
      html += '<span class="product__price-current">' + formattedPrice + '</span>';
    }
    this.priceEl.innerHTML = html;
    if (this.stickyPrice) this.stickyPrice.textContent = formattedPrice;
    if (this.submitBtn) this.submitBtn.dataset.price = formattedPrice;
    if (this.submitBtnText && variant.available) {
      this.submitBtnText.textContent = (this.submitBtn.dataset.textAdd || 'Add to cart') + ' \u2014 ' + formattedPrice;
    }
  }

  updateAvailability(variant) {
    if (!this.submitBtn) return;
    let formattedPrice = this.submitBtn.dataset.price || '';
    this.submitBtn.disabled = !variant.available;
    if (this.submitBtnText) {
      if (variant.available) {
        this.submitBtnText.textContent = (this.submitBtn.dataset.textAdd || 'Add to cart') + (formattedPrice ? ' \u2014 ' + formattedPrice : '');
      } else {
        this.submitBtnText.textContent = this.submitBtn.dataset.textSoldOut || 'Sold out';
      }
    }
    if (this.stickyBtn) this.stickyBtn.disabled = !variant.available;
    this.updateStock(variant);
  }

  updateStock(variant) {
    let stockEl = document.getElementById('product-stock-' + this.sectionId);
    if (!stockEl) return;
    let lowThreshold = parseInt(stockEl.dataset.lowThreshold || '5', 10);
    let pillClass, text;
    if (!variant.available) {
      pillClass = 'product__stock-pill product__stock-pill--out';
      text      = stockEl.dataset.textOut || 'Sold out';
    } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= lowThreshold) {
      pillClass       = 'product__stock-pill product__stock-pill--low';
      let lowTemplate = stockEl.dataset.textLow || 'Only __COUNT__ left';
      text            = lowTemplate.replace('__COUNT__', variant.inventory_quantity);
    } else {
      pillClass = 'product__stock-pill product__stock-pill--in';
      text      = stockEl.dataset.textIn || 'In stock';
    }
    stockEl.innerHTML = '<span class="' + pillClass + '"><span class="product__stock-dot" aria-hidden="true"></span>' + text + '</span>';
  }

  updateSKU(variant) {
    if (!this.skuEl) return;
    let skuSpan = this.skuEl.querySelector('span');
    if (skuSpan) skuSpan.textContent = variant.sku || '';
    this.skuEl.hidden = !variant.sku;
  }

  updateStickyBar(variant) {
    let price = this.formatMoney(variant.price);
    if (this.stickyPrice) this.stickyPrice.textContent = price;
    if (this.stickyBtn)   this.stickyBtn.disabled = !variant.available;
  }

  updateVariantImage(variant) {
    if (!this.galleryEl) return;
    let mediaList = this.galleryEl.querySelectorAll('.product__media');
    let sectionEl = this.closest('[data-hide-variant-media]');
    let hideVariant = sectionEl && sectionEl.dataset.hideVariantMedia === 'true';

    if (hideVariant && variant.featured_image) {
      let featuredId  = String(variant.featured_image.id);
      let variantIds  = [featuredId];
      mediaList.forEach(function(media) {
        media.classList.toggle('product__media--variant-hidden',
          !!(media.dataset.imageId && !variantIds.includes(media.dataset.imageId))
        );
      });
    } else {
      mediaList.forEach(function(media) { media.classList.remove('product__media--variant-hidden'); });
    }

    if (!variant.featured_image) return;
    let imageId     = String(variant.featured_image.id);
    let targetIndex = -1;
    mediaList.forEach(function(media, idx) {
      if (media.dataset.imageId === imageId) targetIndex = idx;
    });
    if (targetIndex >= 0) this.switchGalleryImage(targetIndex);
  }

  updateURL(variant) {
    let url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /* ── Gallery ──────────────────────────────────────────────── */

  setupGallery() {
    this.dots = document.querySelectorAll('#product-dots-' + this.sectionId + ' .product__gallery-dot');

    this.thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        this.switchGalleryImage(parseInt(thumb.dataset.index));
      }.bind(this));
    }, this);

    this.dots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        this.switchGalleryImage(parseInt(dot.dataset.dotIndex));
      }.bind(this));
    }, this);

    if (!this.galleryEl) return;
    let touchStartX = 0;
    let touchStartY = 0;
    let isDragging  = false;

    this.galleryEl.addEventListener('touchstart', function(e) {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      isDragging  = false;
    }, { passive: true });

    this.galleryEl.addEventListener('touchmove', function(e) {
      let dx = Math.abs(e.touches[0].clientX - touchStartX);
      let dy = Math.abs(e.touches[0].clientY - touchStartY);
      if (dx > dy && dx > 8) isDragging = true;
    }, { passive: true });

    this.galleryEl.addEventListener('touchend', function(e) {
      if (!isDragging) return;
      let dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) < 40) return;
      let mediaItems   = this.galleryEl.querySelectorAll('.product__media');
      let currentIndex = 0;
      mediaItems.forEach(function(m, i) { if (m.classList.contains('is-active')) currentIndex = i; });
      let total = mediaItems.length;
      this.switchGalleryImage(dx < 0 ? (currentIndex + 1) % total : (currentIndex - 1 + total) % total);
    }.bind(this));
  }

  switchGalleryImage(index) {
    if (!this.galleryEl) return;
    let mediaItems   = this.galleryEl.querySelectorAll('.product__media');
    let total        = mediaItems.length;
    let clampedIndex = ((index % total) + total) % total;
    let targetMedia  = mediaItems[clampedIndex];
    mediaItems.forEach(function(m, i) { m.classList.toggle('is-active', i === clampedIndex); });
    if (window.innerWidth >= 900 && targetMedia) {
      targetMedia.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    this.thumbnails.forEach(function(t, i) {
      t.classList.toggle('is-active', i === clampedIndex);
      t.setAttribute('aria-pressed', i === clampedIndex);
      if (i === clampedIndex) t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    });
    if (this.dots) {
      this.dots.forEach(function(d, i) { d.classList.toggle('is-active', i === clampedIndex); });
    }
  }

  /* ── Sticky ATC bar ───────────────────────────────────────── */

  setupStickyBar() {
    let stickyBar = document.getElementById('product-sticky-' + this.sectionId);
    let addBtn    = this.submitBtn;
    if (!stickyBar || !addBtn) return;

    let observer = new IntersectionObserver(function(entries) {
      let visible = !entries[0].isIntersecting;
      stickyBar.hidden = !visible;
      stickyBar.toggleAttribute('data-visible', visible);
      stickyBar.setAttribute('aria-hidden', !visible);
    }, { threshold: 0, rootMargin: '0px 0px -80px 0px' });
    observer.observe(addBtn);

    if (this.stickyBtn) {
      this.stickyBtn.dataset.textAdd = this.submitBtnText ? this.submitBtnText.textContent : '';
      this.stickyBtn.addEventListener('click', function() {
        if (addBtn) addBtn.click();
      });
    }
  }

  /* ── Share button ─────────────────────────────────────────── */

  setupShare() {
    let shareBtn = document.getElementById('product-share-' + this.sectionId);
    let confirm  = document.getElementById('share-confirm-' + this.sectionId);
    if (!shareBtn) return;
    shareBtn.addEventListener('click', function() {
      navigator.clipboard.writeText(shareBtn.dataset.url).then(function() {
        if (confirm) {
          confirm.hidden = false;
          setTimeout(function() { confirm.hidden = true; }, 2500);
        }
      });
    });
  }

  /* ── ATC submit ───────────────────────────────────────────── */

  async onSubmit(e) {
    e.preventDefault();
    if (!this.submitBtn || this.submitBtn.disabled) return;

    this.setLoading(true);
    if (this.errorEl) this.errorEl.hidden = true;

    let id       = parseInt(this.form.querySelector('[name="id"]').value, 10);
    let qtyInput = this.form.querySelector('[name="quantity"]');
    let qty      = parseInt(qtyInput ? qtyInput.value : 1, 10) || 1;

    try {
      let resp = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify({ items: [{ id: id, quantity: qty }] })
      });
      if (!resp.ok) {
        let errData = await resp.json();
        throw new Error(errData.description || 'Add to cart failed');
      }
      let data      = await resp.json();
      let addedItem = data.items && data.items[0] ? data.items[0] : {};
      this.announce((addedItem.title || '') + ' added to cart');
      document.dispatchEvent(new CustomEvent('theme:cart:item-added', { detail: addedItem }));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (err) {
      if (this.errorEl) this.errorEl.hidden = false;
      this.announce(err.message || 'Error adding to cart', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading) {
    if (!this.submitBtn) return;
    this.submitBtn.toggleAttribute('data-loading', loading);
    this.submitBtn.disabled = loading;
    if (this.submitBtnSpinner) this.submitBtnSpinner.hidden = !loading;
  }

  /* ── Quantity stepper ─────────────────────────────────────── */

  setupQuantity() {
    this.querySelectorAll('.quantity-selector__btn').forEach(function(btn) {
      btn.addEventListener('click', function() {
        let input = this.querySelector('.quantity-selector__input');
        if (!input) return;
        let val = parseInt(input.value) || 1;
        input.value = btn.dataset.action === 'plus' ? val + 1 : Math.max(1, val - 1);
      }.bind(this));
    }, this);
  }

  /* ── Screen-reader announcer ──────────────────────────────── */

  announce(message, type) {
    let regionId = type === 'error' ? 'sr-page-status' : 'sr-cart-status';
    let region   = document.getElementById(regionId);
    if (!region) return;
    region.textContent = '';
    requestAnimationFrame(function() { region.textContent = message; });
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
